import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';

import { type Brief, userAtom } from '@/components/with_auth/State';
import { BIO_NAMES } from '@/components/onboarding/Shared';
import PanelContainer from '@/components/commons/panel_container';
import NavButtons from '@/components/bio/NavigationButtons';
import BioPageHeader from '@/components/bio/BioPageHeader';
import personalizeStyles from '@/components/bio/Personalize.module.css';
import routes from '@/services/routes';
import { type bioPageProps } from '@/types/globals';
import BioBrief from '@/components/bio/BioBrief';
import { UPDATE_USER } from '@/components/onboarding/Actions';

function ReviewYourBioPanel({
    isModal,
    extendContinue,
    overrideBack,
    openBrief,
    openModal,
}: bioPageProps) {
    const router = useRouter();

    const [user, setUser] = useRecoilState(userAtom);
    const [brief, setCurrentBrief] = useState<Brief>(openBrief
        ? user.briefs.find((item) => item.id === openBrief.briefIndex) : user.briefs[0]);

    useEffect(() => {
        const currentBrief: Brief = openBrief
            ? user.briefs.find((item) => item.id === openBrief.briefIndex) : user.briefs[0];
        if (currentBrief) {
            setCurrentBrief(currentBrief);
        }
    }, [openBrief, user]);

    const [updateUser] = useMutation(UPDATE_USER);

    const handleContinue = async () => {
        // Update user onboarding state
        const response = await updateUser({
            variables: {
                updateUserInput: {
                    attributes: {
                        hasCreatedBio: true,
                    },
                },
            },
        });
        const newUser = response?.data?.updateUser?.user;
        if (newUser) {
            setUser(newUser);
        }

        if (isModal && extendContinue) {
            extendContinue();
        } else {
            router.push(routes.BIO_COMPLETE_SHARE);
        }
    };

    const handleBackNavigation = () => {
        if (isModal && overrideBack) {
            overrideBack(false);
        } else {
            router.push(routes.BIO_SOCIAL_LINKS);
        }
    };

    return (
        <>
            <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8 sm:pb-0" step={5} totalSteps={5} isModal={isModal}>
                <div className="w-full py-2 pb-0 px-2 sm:px-5">
                    <BioPageHeader
                        header="How&apos;s this for your brief?"
                        subHeader="Based on everything you've told us, here's a preview of your brief. To make changes, click &quot;Back&quot; and revisit the content. If you like what you see, click &quot;Publish.&quot;"
                        subHeaderWidth="max-w-[677px]"
                    />
                    <NavButtons
                        containerStyles={`max-h-[42px] top-[100px] mt-0 ${personalizeStyles.top}  `}
                        handleBackNavigation={handleBackNavigation}
                        handleContinue={handleContinue}
                        review
                        isEdit={false}
                    />
                </div>
            </PanelContainer>
            <div className="max-w-[1200px] mx-auto mt-[45px] sm:mt-0">
                <BioBrief brief={brief} user={user} openModal={openModal} />
            </div>
        </>
    );
}

export default ReviewYourBioPanel;
